import { useMutation } from '@apollo/client';
import React, { useContext, useState } from 'react';
import { Button, Spinner } from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import momentUtc from '../../utils/DateFormatter';
import {
  DELETE_NOTIFICATION,
  UPDATE_NOTIFICATION_ORDINAL,
} from '../../utils/statApi/NotificationsApi';
import StatDateFormats from '../../utils/types/DateFormats';
import ShimmeredDetailsListWrapper from '../common/DetailsLists/ShimmeredDetailsListWrapper';
import FullWidthHeader from '../common/headers/FullWidthHeader';
import Subheader from '../common/headers/Subheader';
import { mapToNotificationItems, IStatNotification, Toast } from './StatNotification';
import getColumns from './NotificationsConfig';
import {
  bodyContentContainer,
  iconButtonStyles,
  rowCellLinkActionButton,
} from '../../app/common/styles/CommonStyleObjects';
import CanAccess from '../common/canAccess/CanAccess';
import DeleteConfirmationDialog from '../common/DeleteConfirmationDialog';
import PageHeaderWithActionButtons from '../common/headers/PageHeaderWithActionButtons';
import NotificationEditPanel from './NotificationsEditPanel';
import NotificationContext from './NotificationContext';
import { ListReorderButtons } from '../common/DetailsLists/listReorderButtons/ListReorderButtons';
import useToast from '../../utils/hooks/useToast';

const NotificationsPage = (): JSX.Element => {
  const [loadingIndex, setLoadingIndex] = useState<number>(null);
  const [selectedNotification, setSelectedNotification] = useState<IStatNotification>(null);
  const [isAddPanelOpen, setIsAddPanelOpen] = useState<boolean>(false);
  const [notificationToDelete, setNotificationToDelete] = useState<IStatNotification>(null);
  const { createToast } = useToast();
  const { statNotifications, setNotifications, setStatNotifications } =
    useContext(NotificationContext);

  const [updateNotificationOrdinal, { error: ordinalUpdateError }] = useMutation(
    UPDATE_NOTIFICATION_ORDINAL,
    {
      onCompleted: (res) => {
        setLoadingIndex(null);
        const newNotifications = mapToNotificationItems(res.updateNotificationOrdinal);
        setStatNotifications(res.updateNotificationOrdinal);
        setNotifications(newNotifications);
      },
      onError: (error) => {
        const toast: Toast = {
          id: Math.random(),
          content: ordinalUpdateError.message,
          header: 'Notifications',
          iconName: 'ErrorBadge',
        };
        createToast(toast);
      },
    },
  );

  const [deleteNotification, { loading: deleteLoading }] = useMutation(DELETE_NOTIFICATION, {
    onCompleted: (res) => {
      const newStatNotifications = statNotifications.filter(
        (no) => no.id !== res.deleteNotification.id,
      );
      const newNotifications = mapToNotificationItems(newStatNotifications);
      setStatNotifications(newStatNotifications);
      setNotifications(newNotifications);
      setNotificationToDelete(null);
    },
    onError: (error) => {
      const toast: Toast = {
        id: Math.random(),
        content: error.message,
        header: 'Notifications',
        iconName: 'ErrorBadge',
      };
      createToast(toast);
    },
  });

  const renderHeader = (): JSX.Element => {
    const button = (
      <CanAccess requestedAction="Notification:AddEdit">
        <Button
          appearance="primary"
          onClick={() => {
            setIsAddPanelOpen(true);
          }}
        >
          Add New Notification
        </Button>
      </CanAccess>
    );

    return <PageHeaderWithActionButtons title="Notifications" actionButtons={button} />;
  };
  const renderSubtitle = (): JSX.Element => <Subheader title="STAT Notifications" />;

  const onRenderStartDate = (notification: IStatNotification): JSX.Element => {
    return <span>{`${momentUtc(notification.startDate, StatDateFormats.DayBreakout)}`}</span>;
  };

  const onRenderEndDate = (notification: IStatNotification): JSX.Element => {
    return <span>{`${momentUtc(notification.endDate, StatDateFormats.DayBreakout)}`}</span>;
  };

  const onRenderNotificationType = (notification: IStatNotification): JSX.Element => {
    return <span>{notification.notificationType?.fieldName}</span>;
  };

  const onSwapItems = (currentIemIndex: number, swapItemIndex: number) => {
    const notification = statNotifications[currentIemIndex];
    setLoadingIndex(currentIemIndex);
    updateNotificationOrdinal({
      variables: {
        notificationId: notification.id,
        swappedNotificationId: statNotifications[swapItemIndex].id,
      },
    });
  };

  const onRenderNotificationHeader = (notification: IStatNotification): JSX.Element => {
    return (
      <Button
        appearance="transparent"
        style={rowCellLinkActionButton.root as React.CSSProperties}
        onClick={() => {
          setSelectedNotification(notification);
          setIsAddPanelOpen(true);
        }}
      >
        {notification.header}
      </Button>
    );
  };

  const onRenderOrdinal = (notification: IStatNotification) => {
    const notificationIndex = statNotifications.findIndex((no) => no.id === notification.id);
    return (
      <>
        {notificationIndex === loadingIndex && <Spinner aria-label="Loading" />}
        {notificationIndex !== loadingIndex && (
          <ListReorderButtons
            listItemIndex={notificationIndex}
            totalItemCount={statNotifications.length}
            onSwapDown={onSwapItems}
            onSwapUp={onSwapItems}
          />
        )}
      </>
    );
  };

  const deleteButtonClick = (notification: IStatNotification): void => {
    setNotificationToDelete(notification);
  };

  const hideDeleteDialog = (): void => {
    setNotificationToDelete(null);
  };

  const deleteConfirmClick = (): void => {
    if (!notificationToDelete) return;
    deleteNotification({
      variables: {
        notificationId: notificationToDelete.id,
      },
    });
  };

  const deleteButtonColumn = (notification: IStatNotification): JSX.Element => (
    <Button
      appearance="subtle"
      aria-label="Delete Notification"
      icon={<DeleteRegular color={iconButtonStyles.color} />}
      onClick={() => deleteButtonClick(notification)}
    />
  );

  const columns = getColumns({
    onRenderNotificationHeader,
    onRenderStartDate,
    onRenderEndDate,
    onRenderNotificationType,
    onRenderOrdinal,
    deleteButtonColumn,
  });

  const onPanelClose = () => {
    setIsAddPanelOpen(false);
    setSelectedNotification(null);
  };

  return (
    <>
      <FullWidthHeader title={renderHeader} subtitle={renderSubtitle} />
      <div className={`${bodyContentContainer}  ms-depth-4`}>
        <ShimmeredDetailsListWrapper
          columns={columns}
          items={statNotifications || []}
          // enableShimmer={loading}
          singularListLabel="Notification"
          pluralListLabel="Notifications"
        />
      </div>
      {isAddPanelOpen && (
        <NotificationEditPanel notification={selectedNotification} closePanel={onPanelClose} />
      )}
      <DeleteConfirmationDialog
        open={!!notificationToDelete}
        message="Are you sure you want to delete this notification?"
        isConfirming={deleteLoading}
        onConfirm={deleteConfirmClick}
        onCancel={hideDeleteDialog}
      />
    </>
  );
};

export default NotificationsPage;
